import React from 'react';
import { observer } from 'mobx-react';
import { useRouter } from 'next/compat/router';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import { useKioskLiveness } from 'src/hooks/use-kiosk-liveness';

import { Header } from 'src/dispensary/core-menu/components/header';
import { ConciergeFab } from 'src/dispensary/core-menu/components/concierge-fab';
import { useIsConciergeEnabled } from 'src/concierge/use-is-concierge-enabled';
import { useIsKioskConciergeEnabled } from 'src/concierge/use-is-kiosk-concierge-enabled';
import { useKioskLdDispensaryReidentify } from 'src/concierge/use-kiosk-ld-dispensary-reidentify';
import { useIsLdContextForDispensary } from 'src/concierge/use-is-ld-identified-for-dispensary';
import { useLatchedFlag } from 'src/hooks/use-latched-flag';
import { KIOSK_CONCIERGE_ROLLOUT_FLAG } from '@dutchie/common/utils/concierge-rollout';
import { KioskConciergeEntry } from 'src/dispensary/core-menu/components/kiosk-concierge-entry';
import { KioskProLayout } from 'src/components/kiosk-pro/kiosk-pro-layout';
import CoreMenuFooter from 'dispensary/core-menu/components/core-menu-footer';

import { useCurrentDispensary } from 'src/dispensary/hooks/use-current-dispensary';
import useUI from 'src/hooks/use-ui';
import { useOrderTypeTakeoverPrompt } from 'src/hooks/use-order-type-takeover-prompt';
import { WhoopsContent, WhoopsReason } from 'src/components/whoops';

import { LayoutWrapper } from './layout-wrapper';

type CoreMenuLayoutProps = {
  children: React.ReactNode;
};

export const CoreMenuLayout = observer(({ children }: CoreMenuLayoutProps): JSX.Element | null => {
  const { dispensary, loading, error } = useCurrentDispensary();
  const router = useRouter();
  const isKioskProEnabled = useKioskPro();
  const UI = useUI();
  const { onCollectionsPage, isKiosk, isConciergeActive } = UI;
  // Concierge FAB: storefront only. Per product decision the fixed-position
  // floating button never shows on embedded (iframe) menus, flag on or not,
  // because a fixed element does not behave reliably inside an auto-resized,
  // host-page-controlled iframe. The embedded + small-viewport case is covered
  // instead by ConciergeEntryBanner (see navigation.tsx), the full-width
  // non-floating entry point added for ECOMM-12462.
  const isConciergeFabEnabled = useIsConciergeEnabled(dispensary, UI, { allowEmbedded: false });
  // Shell entry covers custom navigation; the header owns the fallback desktop entry.
  // Kiosk Pro supplies its own sidebar entry.
  const isKioskConciergeEnabled = useIsKioskConciergeEnabled(dispensary) && isKiosk;
  // The footer follows ConciergePage's latch: a late LD identify that drops the flag must not
  // bring the footer back under the open page. The scope is this route visit for this store,
  // so leaving the route clears it and the kill switch applies on the next navigation.
  const isOnKioskConciergeRoute = isKiosk && isConciergeActive;
  const isLdContextForDispensary = useIsLdContextForDispensary(dispensary?.id);
  const isKioskConciergeLatched =
    useLatchedFlag(
      KIOSK_CONCIERGE_ROLLOUT_FLAG,
      isOnKioskConciergeRoute ? `concierge:${dispensary?.id ?? ''}` : null,
      isOnKioskConciergeRoute && isLdContextForDispensary
    ) && dispensary?.storeSettings?.enableKioskConcierge === true;
  const hideKioskConciergeFooter = isOnKioskConciergeRoute && (isKioskConciergeEnabled || isKioskConciergeLatched);

  // Pass the dispensary id already fetched above so the liveness ping doesn't run a second query.
  // ECOMM-12178: the PIN is optional -- this only reports liveness for an already-claimed
  // device, it never blocks the menu (see `/kiosks/[cName]/setup` for claiming a PIN).
  useKioskLiveness(dispensary?.id ?? null);
  // LD identify calls are unordered; on kiosk without the bootstrapper worker (no reactEnv.dispensaryId),
  // correct a context that lost the dispensary so dispensary-targeted flags are not stuck false.
  useKioskLdDispensaryReidentify(dispensary, isKiosk);

  useOrderTypeTakeoverPrompt();

  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing -- boolean expression */
  const hideCategories =
    isKioskProEnabled || !(router?.route.includes('products') || router?.route.includes('brands') || onCollectionsPage);
  /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */

  if (isKioskProEnabled && isKiosk && dispensary) {
    return <KioskProLayout dispensary={dispensary}>{children}</KioskProLayout>;
  }

  if (error) {
    console.error('Error fetching dispensary', error);
    if (!dispensary) {
      return <WhoopsContent reason={WhoopsReason.serverError} />;
    }
  }

  if (!loading && !dispensary) {
    console.error('Dispensary not found');
    return <WhoopsContent reason={WhoopsReason.pageNotFound} />;
  }

  return (
    <LayoutWrapper>
      <Header dispensary={dispensary} />

      {isConciergeFabEnabled && <ConciergeFab cName={dispensary?.cName ?? null} />}

      <KioskConciergeEntry cName={dispensary?.cName ?? null} enabled={isKioskConciergeEnabled} />

      {children}

      {/* The kiosk concierge page is an exact-viewport box under the sticky header;
          the footer would add a dead scroll region below it. Pro suppresses its own
          footer via hideFooter. */}
      {!hideKioskConciergeFooter && <CoreMenuFooter hideCategories={hideCategories} />}
    </LayoutWrapper>
  );
});
